import { ref, inject } from "vue";
import { defineStore } from "pinia";
import { QuizQuestion } from "@/types/quiz/QuizQuestion";
import { MarketingPreferences } from "@/types/user/MarketingPreferences";
import { AdditionalServiceRequirements } from "@/types/user/AdditionalServiceRequirements";
import { BillingAddress } from "@/types/user/BillingAddress";
import { HomemoverApiClient } from "@/services/HomemoverApiClient";
import { useUserStore } from "./UserStore";
import { UserProgress } from "@/types/progress/UserProgress";
import { readCookieAsString } from '@/helpers/cookies/JsonCookieReader';
import { writeCookie } from '@/helpers/cookies/JsonCookieWriter';
import { Mutex } from "async-mutex";
import { QuizAnswer } from "@/types/progress/QuizAnswerEnum";
import { MeterReadings } from "@/types/user/MeterReadings";

export const useUserProgressStore = defineStore('userProgressStore', () => {
    const userProgress = ref<UserProgress>();
    const homemoverApiClient = inject('HomemoverApiClient') as HomemoverApiClient;
    const userStore = useUserStore();
    const failedFetch = ref(false);
    const urlUserId = ref('');
    const userProgressCookieName = 'pb-homemover-progress-id';
    const mutex = new Mutex();
    const loadingPageComplete = ref(false);

    const getUserIdFromCookie = () => {
        return readCookieAsString(userProgressCookieName);
    }

    const updateUserIdFromCookie = () => {
        writeCookie(userProgressCookieName, urlUserId.value);
    }

    const fetchUserProgress = async () => {
        try {
            userProgress.value = await homemoverApiClient.httpPost<UserProgress>(`/app/user/progression/retrieve`, { userId: getUserIdFromCookie() });
            failedFetch.value = false;
        } catch (err) {
            /* Will error if user has no progression previously cached or if the api is down
             * In both situations we want the user to be able to continue through the app */
            failedFetch.value = true;
        }
    };

    /** Updates the user's current progress */
    const updateUserProgress = async () => {
        await mutex.runExclusive(async () => {
            try {
                await homemoverApiClient.httpPostVoid(`/app/user/progression/update`, userProgress.value);
            } catch (err) {
                console.error(err);
            }
        });
    }

    const updateSFWithContractExpiry = async (contractExpiry : string|null) => {
        await mutex.runExclusive(async () => {
            try {
                await homemoverApiClient.httpPostVoid(`/app/contractexpiry`, {"contractExpiryDate":contractExpiry, "switchId" : getUserIdFromCookie() });
            } catch (err) {
                console.error(err);
            }
        });
    }
    const updateSFWithStayedAtProperty = async (stayedAtProperty : boolean|null) => {
        await mutex.runExclusive(async () => {
            try {
                await homemoverApiClient.httpPostVoid(`/app/renewalmovedproperty`, {"stayedAtProperty":stayedAtProperty, "switchId" : getUserIdFromCookie() });
            } catch (err) {
                console.error(err);
            }
        });
    }

    /** Initialises the User Progress model */
    const initUserProgressStore = async () => {
        if (!userProgress.value) {
            await fetchUserProgress();

            //If no data is returned from the api, a default user progression value is assigned to the user to avoid null ref errors
            if (failedFetch.value) {
                userProgress.value = createNewUserProgressModel();
            }
        }
      
        return userProgress.value;
    }

    /** Creates a new UserProgress model populated with default values */
    const createNewUserProgressModel = (): UserProgress => {
        return {
            userId: urlUserId.value,
            quizStep: {
                quizQuestions: [
                    {
                        number: QuizAnswer.livingPreferenceAnswer,
                        text: 'Who do you live with?',
                        answers: [
                            {
                                id: 1,
                                text: 'I live by myself'
                            },
                            {
                                id: 2,
                                text: 'I live with another person'
                            },
                            {
                                id: 3,
                                text: 'I live with my family'
                            },
                            {
                                id: 4,
                                text: 'I live in a house share'
                            },
                        ],
                        selectedAnswerId: 1
                    },
                    {
                        number: QuizAnswer.atHomeAnswer,
                        text: 'When are you mainly at home?',
                        answers: [
                            {
                                id: 1,
                                text: 'Mostly mornings & evenings'
                            },
                            {
                                id: 2,
                                text: 'All the time'
                            },
                            {
                                id: 3,
                                text: 'Mostly during the day'
                            }
                        ],
                        selectedAnswerId: 1
                    },
                    {
                        number: QuizAnswer.broadbandUsagePreference,
                        text: 'What do you use broadband for?',
                        answers: [
                            {
                                id: 1,
                                text: 'General browsing and streaming'
                            },
                            {
                                id: 2,
                                text: 'Homeworking, streaming and browsing'
                            },
                            {
                                id: 3,
                                text: 'Gaming, streaming and browsing'
                            }
                        ],
                        selectedAnswerId: 1
                    }
                ]
            },
            energyStep: {
                selectedEnergyDealId: undefined,
            },
            broadbandStep: {
                selectedBroadbandDealId: undefined
            },
            tvStep: {
                selectedTvDealId: undefined
            },
            streamingStep: {
                selectedStreamingDealIds: undefined,
                selectedStreamingDealNames: undefined
            }, simCardStep: {
                selectedSimCardDealIds: undefined,
                selectedSimCardDealNames: undefined
            },
            tvMiniBoxStep: {
                selectedTvMiniBoxDealIds: undefined,
                selectedTvMiniBoxDealNames: undefined
            },
            wasteStep: {
                selectedWasteDealId: undefined
            },
            securityStep: {
                selectedSecurityDealId: undefined
            },
            preferencesStep: {
                marketingPreferences: {
                    email: false,
                    sms: false,
                    mobile: false,
                    post: false,
                    doorToDoor: false,
                    landline: false,
                    additionalServices: false
                },
                additionalServiceRequirements: {
                    priorityServices: false,
                    specialServices: false,
                    LanguageDifficulty: false,
                    MobilityImpairment: false,
                    HearingImpairment: false,
                    LearningDifficulty: false,
                    AdvancingAge: false,
                    VisionImpairment: false,
                    SpeechImpairment: false,
                    MentalHealth: false
                },
                rewardsSignUp: false,
                smartConsentObtained: false
            },
                electricReading1: '',
                electricReading2: '',
                electricReading3: '',
                gasReading: '',
                contractExpiry: null,
            currentStep: 0,
            securityAnswer: '',
            stayedAtProperty: null
        }
    }

    /** Retrieves the current user's progress through the app */
    const getUserProgress = (): UserProgress => {
        // To avoid null ref errors, ensure store has been initialise
        if (!userProgress.value) {
            initUserProgressStore();
        }

        if (!userProgress.value) {
            throw 'Unable to get user progress'
        }

        return userProgress.value;
    };

    /** Stores the id of the current user*/
    const setUserId = async (userId: string) => {
        // Avoid api errors by only calling api if user id has been passed
        if (!userId) {
            return;
        }

        urlUserId.value = userId;

        if (!userProgress.value) {
            updateUserIdFromCookie();
            return;
        }

        userProgress.value.userId = userId;
        await updateUserProgress();
    }

    /** Stores the user's answers to the quiz in step 2 */
    const storeQuizAnswers = async (quiz: QuizQuestion[]) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.quizStep = { quizQuestions: quiz };
        await updateUserProgress();
    }

    /** Stores the id of the energy deal the user has selected */
    const storeSelectedEnergyDealId = async (selectedDealId: string) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.energyStep.selectedEnergyDealId = selectedDealId;
        await updateUserProgress();
    }

    /** Clears the entire media order in one update, including saved add-ons. */
    const clearMediaSelections = async () => {
        if (!userProgress.value) {
            return;
        }

        const progress = userProgress.value;
        const selections = [
            progress.broadbandStep?.selectedBroadbandDealId,
            progress.tvStep?.selectedTvDealId,
            progress.tvMiniBoxStep?.selectedTvMiniBoxDealIds,
            progress.tvMiniBoxStep?.selectedTvMiniBoxDealNames,
            progress.streamingStep?.selectedStreamingDealIds,
            progress.streamingStep?.selectedStreamingDealNames,
            progress.simCardStep?.selectedSimCardDealIds,
            progress.simCardStep?.selectedSimCardDealNames
        ];
        if (selections.every(selection => selection === undefined)) {
            return;
        }

        progress.broadbandStep = { selectedBroadbandDealId: undefined };
        progress.tvStep = { selectedTvDealId: undefined };
        progress.tvMiniBoxStep = { selectedTvMiniBoxDealIds: undefined, selectedTvMiniBoxDealNames: undefined };
        progress.streamingStep = { selectedStreamingDealIds: undefined, selectedStreamingDealNames: undefined };
        progress.simCardStep = { selectedSimCardDealIds: undefined, selectedSimCardDealNames: undefined };
        await updateUserProgress();
    };

    /** Stores the id of the broadband deal the user has selected */
    const storeSelectedBroadbandDealId = async (selectedDealId?: string) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.broadbandStep.selectedBroadbandDealId = selectedDealId;
        await updateUserProgress();
    }

    const storeStreamingDealsIdsNames = async (selectedDealIds?: string, selectedDealNames?: string[]) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.streamingStep = { selectedStreamingDealIds: selectedDealIds, selectedStreamingDealNames: selectedDealNames };

        await updateUserProgress();
    }

    const storeSimCardIdsNames = async (selectedIds?: string[], selectedNames?: string[]) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.simCardStep = { selectedSimCardDealIds: selectedIds, selectedSimCardDealNames: selectedNames };

        await updateUserProgress();
    };

    /** Stores the id of the tv deal the user has selected */
    const storeSelectedTvId = async (selectedDealId?: string) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.tvStep = { selectedTvDealId: selectedDealId };
        await updateUserProgress();
    }

    const storeSelectedMiniBoxIdsNames = async (selectedIds?: string[], selectedNames?: string[]) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.tvMiniBoxStep = { selectedTvMiniBoxDealIds: selectedIds, selectedTvMiniBoxDealNames: selectedNames };
        await updateUserProgress();
    };

    /** Stores the id of the waste deal the user has selected */
    const storeSelectedWasteDealId = async (selectedDealId?: string) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.wasteStep.selectedWasteDealId = selectedDealId;
        await updateUserProgress();
    }

        /** Stores the id of the security deal the user has selected */
        const storeSelectedSecurityDealId = async (selectedDealId?: string) => {
            if (!userProgress.value) {
                return;
            }
            // Since this is a new step create the new step if it is blank for the cross over period 
            // where suers have gone into the app but not reached the security step before we make it live
            if(userProgress.value.securityStep == null) {
                userProgress.value.securityStep = {
                    selectedSecurityDealId: undefined
                }
            }
            userProgress.value.securityStep.selectedSecurityDealId = selectedDealId;
            await updateUserProgress();
        }
    /** Stores the user's marketing preferences */
    const storeMarketingPreferences = async (marketingPreferences: MarketingPreferences, rewardsSignUp: boolean, securityAnswer: string) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.preferencesStep.marketingPreferences = marketingPreferences;
        userProgress.value.preferencesStep.rewardsSignUp = rewardsSignUp;
        userProgress.value.securityAnswer = securityAnswer;
        userProgress.value.preferencesStep.smartConsentObtained = true;
        await updateUserProgress();
    }

    /** Stores any additional service requirements the user has */
    const storeAdditionalServiceRequirements = async (requirements: AdditionalServiceRequirements) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.preferencesStep.additionalServiceRequirements.priorityServices = requirements.priorityServices;
        userProgress.value.preferencesStep.additionalServiceRequirements.specialServices = requirements.specialServices;
        userProgress.value.preferencesStep.additionalServiceRequirements.LanguageDifficulty = requirements.LanguageDifficulty;
        userProgress.value.preferencesStep.additionalServiceRequirements.MobilityImpairment = requirements.MobilityImpairment;
        userProgress.value.preferencesStep.additionalServiceRequirements.HearingImpairment = requirements.HearingImpairment;
        userProgress.value.preferencesStep.additionalServiceRequirements.LearningDifficulty = requirements.LearningDifficulty;
        userProgress.value.preferencesStep.additionalServiceRequirements.AdvancingAge = requirements.AdvancingAge;
        userProgress.value.preferencesStep.additionalServiceRequirements.VisionImpairment = requirements.VisionImpairment;
        userProgress.value.preferencesStep.additionalServiceRequirements.SpeechImpairment = requirements.SpeechImpairment;
        userProgress.value.preferencesStep.additionalServiceRequirements.MentalHealth = requirements.MentalHealth;

        await updateUserProgress();
    };

    const storeMeterReadings = async (meterReadings: MeterReadings) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.electricReading1 = meterReadings.electricReading1;
        userProgress.value.electricReading2 = meterReadings.electricReading2;
        userProgress.value.electricReading3 = meterReadings.electricReading3;
        userProgress.value.gasReading = meterReadings.gasReading;
        
        await updateUserProgress();
    };

    const storeContractExpiry = async (contractExpiry: string|null) => {
        if (!userProgress.value) {
            return;
        }

        // Mark contract expiry as null since blank strings can cause issues with date conversions
        if(contractExpiry === '') {
            contractExpiry = null;
        }

        userProgress.value.contractExpiry = contractExpiry;
        await updateSFWithContractExpiry(contractExpiry);
        await updateUserProgress();
    }


    const storeStayedAtProperty = async (stayedAtProperty: boolean| null) => {
        if (!userProgress.value) {
            return;
        }


        userProgress.value.stayedAtProperty = stayedAtProperty;
        await updateSFWithStayedAtProperty(stayedAtProperty);
        await updateUserProgress();
    }

    //These functions are triggered immediately when users access their asscoiated pages 

    const setUserStep = async (step: number) => {
        if (!userProgress.value) {
            return;
        }

        userProgress.value.currentStep = step;
        await updateUserProgress();
    };

    const currentBillingAddress = ref<BillingAddress>();

    const getCurrentBillingAddress = async () => {

        if (!currentBillingAddress.value) {
            const userDetails = await userStore.getCurrentUserDetails();
            if (!userDetails) {
                return {
                    addressLine1: '',
                    addressLine2: '',
                    town: '',
                    county: '',
                    eirCode: ''
                }
            }
            currentBillingAddress.value = {
                addressLine1: userDetails.property.addressLine1,
                addressLine2: userDetails.property.addressLine2,
                town: userDetails.property.town,
                county: userDetails.property.county,
                eirCode: userDetails.property.postcode
            }
            return currentBillingAddress.value
        }

        return currentBillingAddress.value
    };

    const updateCurrentBillingAddress = (newAddress: BillingAddress) => currentBillingAddress.value = newAddress;

    return {
        initUserProgressStore, getUserProgress, setUserId, storeQuizAnswers, storeSelectedEnergyDealId,
        storeSelectedBroadbandDealId, clearMediaSelections, storeSelectedWasteDealId, storeMarketingPreferences,
        storeAdditionalServiceRequirements, getCurrentBillingAddress,
        updateCurrentBillingAddress, getUserIdFromCookie, updateUserIdFromCookie,
        setUserStep, storeSelectedTvId, storeStreamingDealsIdsNames, storeSimCardIdsNames, storeSelectedMiniBoxIdsNames, storeMeterReadings, storeContractExpiry, storeStayedAtProperty,
        storeSelectedSecurityDealId, loadingPageComplete
    }
});
